import DocumentViewerModal from "components/DocumentViewer";
import FileRow from "components/file/file-row";
import FileUpload from "components/FileUpload";
import TeamView from "components/team_view";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useState, useEffect } from "react";
import { formatDay } from "utils/helpers";
import { useTranslation } from "react-i18next";
import {
  updateInternalLegalResearchRequest,
  updateLegalResearchRequest,
} from "services/legalResearchService";

interface singleResearchPaperDetailPops {
  personImg: any;
  research: any;
  fileRowProps?: any;
  onUpdate?: (updatedResearch: any) => void;
}

export default function SingleResearchPapersDetail({
  personImg,
  research,
  fileRowProps,
  onUpdate,
}: singleResearchPaperDetailPops) {
  const { t } = useTranslation();
  const [view, setView] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState(research?.draft_summary || research?.research_topic_abstract || "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { token } = useAuth();
  const { showToast } = useToast();

  // Check if user is Director or Legal Research Coordinator
  const contextData = useAuth();
  const isAuthorizedUser = contextData?.user?.roles?.some(
    (role: string) => role === "LegalResearchCoordinator" || role === "LRDIFUDDirector"
  );

  // Update editedSummary when research prop changes
  useEffect(() => {
    setEditedSummary(research?.draft_summary || research?.research_topic_abstract || "");
  }, [research]);

  // Get team information
  const teamMembers = research.internal_topic_assignment || research.prosecutor_assignments || [];
  const teamLeader = teamMembers.find((p: any) => p.is_team_leader);
  const totalMembers = teamMembers.length;

  // Function to get the file to display based on priority rules
  const getFileToDisplay = () => {
    const allFiles = research?.documents || [];

    // Find and sort amended files (priority 1)
    const amendedFiles = allFiles
      .filter((doc: any) => doc.document_type === "research_amended_file")
      .sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

    if (amendedFiles.length > 0) {
      return { latestFile: amendedFiles[0], previousFiles: amendedFiles.slice(1) };
    }

    // Find and sort draft files (priority 2)
    const draftFiles = allFiles
      .filter((doc: any) => doc.document_type === "research_draft_file")
      .sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

    if (draftFiles.length > 0) {
      return { latestFile: draftFiles[0], previousFiles: draftFiles.slice(1) };
    }

    return { latestFile: null, previousFiles: [] };
  };

  // Get the file to display
  const { latestFile, previousFiles } = getFileToDisplay();

  // Handle save of edited summary and file
  const handleSaveEdit = async () => {
    if (!editedSummary.trim() && !selectedFile) {
      showToast(t("PleaseProvideSummaryOrFile"), "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      
      if (editedSummary.trim()) {
        formData.append("draft_summary", editedSummary.trim());
      }
      
      if (selectedFile) {
        formData.append("research_draft_file", selectedFile);
      }

      let updatedResearch: any;
      
      if (research?.requested_by === "internal") {
        updatedResearch = await updateInternalLegalResearchRequest(
          token,
          research.id,
          formData
        );
      } else {
        updatedResearch = await updateLegalResearchRequest(
          research.id,
          formData,
          token
        );
      }

      if (onUpdate) {
        onUpdate(updatedResearch);
      }

      showToast(t("ResearchUpdatedSuccessfully"), "success");
      setIsEditing(false);
      setSelectedFile(null);
    } catch (error) {
      console.error("Error updating research:", error);
      showToast(t("FailedToUpdateResearch"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditedSummary(research?.draft_summary || research?.research_topic_abstract || "");
    setSelectedFile(null);
    setIsEditing(false);
  };

  return (
    <>
      <div className="flex flex-col bg-white rounded-lg shadow-md px-8 p-6 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-medium text-xl text-[#212121]">
              {research.research_topic}
            </h2>
          </div>
          {/* Edit Button for Director and Legal Research Coordinator */}
          {isAuthorizedUser && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 bg-[#212121] hover:bg-[#141414] text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              {t("EditSummary")}
            </button>
          )}
        </div>

        <div className="flex gap-1 text-[#9D0303] font-medium text-sm mb-1">
          <p>{formatDay(research.updated_at).weekday},</p>
          <p>{formatDay(research.updated_at).rest}</p>
        </div>

        {/* Research Abstract or Draft Summary with proper paragraph formatting */}
        <div className="mt-4 mb-6">
          {isEditing ? (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-base text-[#212121] mb-2">{t("EditSummary")}</p>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[150px]"
                placeholder={t("EnterSummaryPlaceholder")}
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
              />
              
              <div className="mt-4">
                <p className="font-semibold text-base text-[#212121] mb-2">{t("ReuploadResearchDraftFile")}</p>
                <FileUpload
                  onChange={(file) => setSelectedFile(file)}
                  label={t("ResearchDraftFile")}
                  fileTypes=".pdf,.doc,.docx"
                />
              </div>
              
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSaveEdit}
                  disabled={isSubmitting}
                  className="bg-[#212121] hover:bg-[#141414] disabled:bg-gray-400 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  {isSubmitting ? t("Submitting") : t("SaveChanges")}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSubmitting}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md font-medium transition-colors"
                >
                  {t("Cancel")}
                </button>
              </div>
            </div>
          ) : research.research_topic_abstract ? (
            <div className="space-y-4">
              <p className="font-semibold text-base text-[#212121] mb-2">{t("Abstract")}</p>
              {research.research_topic_abstract.split("\n").map(
                (paragraph: string, index: number) =>
                  paragraph.trim() !== "" && (
                    <p
                      key={index}
                      className="font-light text-base text-[#212121] leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  )
              )}
            </div>
          ) : research.draft_summary ? (
            <div className="space-y-4">
              <p className="font-semibold text-base text-[#212121] mb-2">{t("ResearchDraftSummary")}</p>
              {research.draft_summary.split("\n").map(
                (paragraph: string, index: number) =>
                  paragraph.trim() !== "" && (
                    <p
                      key={index}
                      className="font-light text-base text-[#212121] leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  )
              )}
            </div>
          ) : (
            <p className="font-light text-base text-gray-500 italic">
              {t("NoSummaryAvailable")}
            </p>
          )}
        </div>

        {/* Team Section */}
        <div className="flex mt-5 mb-3 rounded-lg p-4 bg-gray-5">
          <div className="flex w-full items-center">
            {/* Team Leader Avatar with badge */}
            <div className="relative mr-4">
              <img
                src={personImg}
                alt={t("TeamLeaderAvatarAlt")}
                width={48}
                height={48}
                className="object-cover rounded-full"
              />
              {totalMembers > 1 && (
                <div className="absolute -bottom-1 -right-1 bg-gray-400 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-medium">
                  +{totalMembers - 1}
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[#0A1D39] font-semibold text-lg">
                  {teamLeader?.prosecutor_name || t("Unknown")}
                </p>
                {totalMembers > 1 && (
                  <span className="text-gray-500 text-sm font-normal">
                    {t("PlusMoreMembers", { count: totalMembers - 1 })}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-4">
                <p className="text-[#9D0303] font-medium text-sm">{t("Researcher")}</p>
                {totalMembers > 0 && (
                  <div className="flex items-center text-gray-600 text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    {t("MemberCount", { count: totalMembers })}
                  </div>
                )}
              </div>
            </div>

            {/* View Team Button */}
            <button
              onClick={() => setView(!view)}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-400 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              title={t("ViewTeam")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <mask
                  id="mask0_9_31087"
                  maskUnits="userSpaceOnUse"
                  x="1"
                  y="5"
                  width="22"
                  height="14"
                >
                  <path
                    d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                    fill="white"
                    stroke="white"
                    strokeWidth="1.33333"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                    fill="black"
                    stroke="black"
                    strokeWidth="1.33333"
                    strokeLinejoin="round"
                  />
                </mask>
                <g mask="url(#mask0_9_31087)">
                  <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                </g>
              </svg>
              <span className="font-medium text-sm">{t("ViewTeam")}</span>
            </button>
          </div>
        </div>

        {/* Document Viewer Section */}
        <div className="flex items-center justify-between p-3 border border-gray-300 rounded-lg gap-2 flex-col mb-2">
          {latestFile && (
            <>
              {/* Latest File - Shown Independently */}
              <div className="w-full">
                <p className="font-semibold text-base text-[#212121] mb-2">{t("LatestResearchDraft")}</p>
                <DocumentViewerModal
                  documents={[latestFile]}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                />
              </div>
              
              {/* Previous Files - Shown in Accordion */}
              {previousFiles.length > 0 && (
                <details className="my-4 w-full">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 bg-gray-100 p-2 rounded-lg hover:bg-gray-200 transition-colors">
                    {t("PreviousVersions", { count: previousFiles.length })}
                  </summary>
                  <div className="mt-2 pl-2">
                    {previousFiles.map((file: any, index: number) => (
                      <div key={file.id || index} className="mb-4 p-2 border border-gray-200 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2">
                          {t("VersionNumber", { 
                            version: previousFiles.length - index,
                            date: new Date(file.created_at).toLocaleDateString() 
                          })}
                        </p>
                        <DocumentViewerModal
                          documents={[file]}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                        />
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      </div>

      {/* Team View Modal */}
      {view && (
        <TeamView
          onClose={() => setView(false)}
          teamMembers={teamMembers}
        />
      )}
    </>
  );
}